import { HOMEWORK_MAX_FIELDS, type HomeworkField, type HomeworkFieldKind } from '@adhd/shared';

/**
 * Editing the boxes a homework asks for.
 *
 * ONE DEFINITION, used when it is set up and again when it is changed. It was
 * written twice for a few minutes and the two had already drifted on whether a
 * field could be removed — which is the whole argument for lifting it out.
 *
 * The kinds are a closed set and this is the only place they are drawn. A model
 * composing a draft picks from exactly these four; there is no path, here or in
 * the schema, to a fifth.
 */
const KINDS: { kind: HomeworkFieldKind; name: string }[] = [
  { kind: 'paragraph', name: 'A box to write in' },
  { kind: 'lines', name: 'A few short lines' },
  { kind: 'scale', name: 'A 0–10 rating' },
  { kind: 'yesno', name: 'Yes or no' },
];

function FieldEditor({
  field,
  drafted,
  onChange,
  onRemove,
}: {
  field: HomeworkField;
  drafted: boolean;
  onChange: (f: HomeworkField) => void;
  onRemove?: () => void;
}): JSX.Element {
  return (
    <div className={drafted ? 'card stack-sm stack card-drafted' : 'card stack-sm stack'}>
      <input
        className="input"
        value={field.label}
        aria-label="What this box asks"
        onChange={(e) => onChange({ ...field, label: e.target.value })}
      />
      <div className="chips">
        {KINDS.map((k) => (
          <button
            key={k.kind}
            className="chip"
            aria-pressed={field.kind === k.kind}
            onClick={() => onChange(changeKind(field, k.kind))}
          >
            {k.name}
          </button>
        ))}
      </div>
      {field.kind === 'lines' && (
        <div className="chips">
          {[2, 3, 4, 5].map((n) => (
            <button
              key={n}
              className="chip"
              aria-pressed={field.count === n}
              onClick={() => onChange({ ...field, count: n })}
            >
              {n} lines
            </button>
          ))}
        </div>
      )}
      {field.kind === 'scale' && (
        <div className="row" style={{ gap: 'var(--space-2)' }}>
          <input
            className="input"
            aria-label="Low end"
            value={field.low}
            onChange={(e) => onChange({ ...field, low: e.target.value })}
          />
          <input
            className="input"
            aria-label="High end"
            value={field.high}
            onChange={(e) => onChange({ ...field, high: e.target.value })}
          />
        </div>
      )}
      {onRemove && (
        <button className="btn btn-ghost btn-compact" onClick={onRemove}>
          Take this box off
        </button>
      )}
    </div>
  );
}

/** Keep the id and the label; swap only what kind of answer it takes. */
function changeKind(f: HomeworkField, kind: HomeworkFieldKind): HomeworkField {
  const base = { id: f.id, label: f.label };
  if (kind === 'lines') return { ...base, kind, count: 3 };
  if (kind === 'scale') return { ...base, kind, low: '', high: '' };
  if (kind === 'yesno') return { ...base, kind };
  return { ...base, kind: 'paragraph' };
}


/** The four kinds, and adding another box. Shared by both screens. */
export function HomeworkFieldList({
  fields,
  drafted,
  onChange,
}: {
  fields: HomeworkField[];
  drafted?: Set<string>;
  onChange: (f: HomeworkField[]) => void;
}): JSX.Element {
  return (
    <section className="stack-sm stack">
      <span className="faint">How you answer it</span>
      {fields.map((f, i) => (
        <FieldEditor
          key={f.id}
          field={f}
          drafted={drafted?.has(f.id) ?? false}
          onChange={(next) => onChange(fields.map((x, j) => (j === i ? next : x)))}
          onRemove={
            fields.length > 1 ? () => onChange(fields.filter((_, j) => j !== i)) : undefined
          }
        />
      ))}
      {fields.length < HOMEWORK_MAX_FIELDS && (
        <button
          className="btn btn-ghost btn-compact"
          onClick={() =>
            onChange([...fields, { kind: 'paragraph', id: `f${fields.length + 1}`, label: '' }])
          }
        >
          Add another box
        </button>
      )}
    </section>
  );
}
